import { useId, useMemo } from 'react'

export interface SparklineProps {
  data: number[]
  /** Any CSS colour, including a `var(--ad-color-1)` token. Defaults to `currentColor`. */
  color?: string
  height?: number
  filled?: boolean
  strokeWidth?: number
  className?: string
}

const VIEW_W = 100
const VIEW_H = 100
const PAD = 6

export function Sparkline({
  data,
  color = 'currentColor',
  height = 36,
  filled = true,
  strokeWidth = 1.5,
  className,
}: SparklineProps) {
  const gradientId = useId()

  const paths = useMemo(() => {
    if (data.length < 2) return null
    const max = Math.max(...data)
    const min = Math.min(...data)
    const span = max - min || 1
    const x = (i: number) => (i / (data.length - 1)) * VIEW_W
    const y = (v: number) => VIEW_H - PAD - ((v - min) / span) * (VIEW_H - PAD * 2)
    const points = data.map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`)
    return {
      line: `M${points.join(' L')}`,
      area: `M0,${VIEW_H} L${points.join(' L')} L${VIEW_W},${VIEW_H} Z`,
    }
  }, [data])

  if (!paths) return null

  return (
    <svg
      className={className}
      width="100%"
      height={height}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ color, display: 'block' }}
    >
      {filled && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.35} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={paths.area} fill={`url(#${gradientId})`} stroke="none" />
        </>
      )}
      <path
        d={paths.line}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
